import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { ExpenseDetailTypeOption } from "@/lib/expenses/expenseCategoriesStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type ExpenseParentCategorySelectProps = {
  value: string;
  categories: ExpenseDetailTypeOption[];
  onChange: (id: string) => void;
  placeholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
};

function itemValue(id: string): string {
  return `category:${id}`;
}

export function ExpenseParentCategorySelect({
  value,
  categories,
  onChange,
  placeholder = "Select category",
  triggerClassName,
  disabled = false,
}: ExpenseParentCategorySelectProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState("");

  const selected = useMemo(
    () => categories.find((c) => c.id === value) ?? null,
    [categories, value],
  );

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>("[data-expense-category-selected='true']")
        ?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, value]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setHighlight(value ? itemValue(value) : "");
      }}
      modal
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "glass-input h-10 w-full justify-between px-3 font-normal text-sm",
            !selected && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="truncate">{selected ? selected.title : placeholder}</span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[100]" align="start" sideOffset={4}>
        <Command
          value={highlight}
          onValueChange={setHighlight}
          filter={(item, search, keywords) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            const haystack = `${item} ${(keywords ?? []).join(" ")}`.toLowerCase();
            return haystack.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search category..." className="h-9 text-xs" />
          <CommandList className="max-h-[240px] overflow-y-auto">
            <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
              No category found.
            </CommandEmpty>
            <CommandGroup>
              {categories.map((c) => {
                const isSelected = c.id === value;
                return (
                  <CommandItem
                    key={c.id}
                    value={itemValue(c.id)}
                    keywords={[c.title, c.code]}
                    data-expense-category-selected={isSelected ? "true" : undefined}
                    onSelect={() => {
                      onChange(c.id);
                      setOpen(false);
                    }}
                    className="text-xs flex items-center justify-between cursor-pointer"
                  >
                    <span className="break-words font-medium">{c.title}</span>
                    <Check
                      className={cn(
                        "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
